// src/pages/administrasi/EventFolderConfig.jsx
//
// "Event & Folder". Every role works in ONE active event at a time, and the
// divisions run it themselves: either one can start the event and end it, and
// each fills in (and verifies) the Drive folder of its own module. The backend
// refuses a second active event, so this page only offers "Mulai event" when
// none is running.
import { useEffect, useState, useCallback } from "react";
import { API_BASE_URL } from "../../utils/config";

const token = () => localStorage.getItem("accessToken");

async function apiFetch(url, opts = {}) {
  const res = await fetch(`${API_BASE_URL}${url}`, {
    ...opts,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token()}`,
      ...(opts.headers || {}),
    },
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(
      data.detail || data.non_field_errors?.[0] || JSON.stringify(data) || "Request failed"
    );
  }
  return res.status === 204 ? null : res.json();
}

const MODULES = [
  { key: "sekretaris", label: "Sekretaris", field: "sekretaris_folder_id", verified: "sekretaris_folder_verified_at" },
  { key: "bendahara", label: "Bendahara", field: "bendahara_folder_id", verified: "bendahara_folder_verified_at" },
];

function VerifyBadge({ verifiedAt }) {
  if (!verifiedAt) return <span className="text-xs text-muted">Belum diverifikasi</span>;
  return (
    <span className="text-xs" style={{ color: "var(--success)" }}>
      ✓ Terverifikasi {new Date(verifiedAt).toLocaleString("id-ID")}
    </span>
  );
}

function FolderCard({ event, mod, mine, onChanged }) {
  const [draft, setDraft] = useState(event[mod.field] || "");
  const [busy, setBusy] = useState("");
  const [msg, setMsg] = useState(null);

  // The event reloads after every save; keep the box in step with it.
  useEffect(() => setDraft(event[mod.field] || ""), [event, mod.field]);

  const save = async () => {
    setBusy("save");
    setMsg(null);
    try {
      await apiFetch(`/api/administrasi/events/${event.id}/`, {
        method: "PATCH",
        body: JSON.stringify({ [mod.field]: draft.trim() }),
      });
      onChanged();
    } catch (e) {
      setMsg({ ok: false, detail: e.message });
    } finally {
      setBusy("");
    }
  };

  const verify = async () => {
    setBusy("verify");
    setMsg(null);
    try {
      const data = await apiFetch(`/api/administrasi/events/${event.id}/verify-folder/`, {
        method: "POST",
        body: JSON.stringify({ module: mod.key }),
      });
      setMsg(data);
      if (data.ok) onChanged();
    } catch (e) {
      setMsg({ ok: false, detail: e.message });
    } finally {
      setBusy("");
    }
  };

  const changed = draft.trim() !== (event[mod.field] || "");

  return (
    <div className="db-card !gap-3">
      <div className="flex items-center justify-between gap-3">
        <span className="text-[15px] font-semibold">Folder {mod.label}</span>
        {!mine && <span className="badge badge-info">Diisi divisi {mod.label}</span>}
      </div>

      {mine ? (
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          className="glass-input font-mono text-xs"
          placeholder="Tempel Folder ID dari URL Drive"
        />
      ) : (
        <div className="text-xs font-mono truncate">
          {event[mod.field] || <span className="text-muted italic">belum diisi</span>}
        </div>
      )}

      <div className="flex items-center gap-2 flex-wrap">
        {mine && (
          <button type="button" onClick={save} disabled={!changed || busy !== ""} className="btn-primary btn-sm">
            {busy === "save" ? "Menyimpan…" : "Simpan"}
          </button>
        )}
        {mine && (
          <button
            type="button"
            onClick={verify}
            disabled={!event[mod.field] || changed || busy !== ""}
            title={changed ? "Simpan dulu, baru diverifikasi" : undefined}
            className="btn-outline btn-sm"
          >
            {busy === "verify" ? "Memeriksa…" : "Test/Verify Folder"}
          </button>
        )}
        <VerifyBadge verifiedAt={event[mod.verified]} />
      </div>

      {msg && (
        <p className="text-xs" style={{ color: msg.ok ? "var(--success)" : "var(--danger)" }}>
          {msg.detail}
        </p>
      )}
    </div>
  );
}

export default function EventFolderConfig() {
  // Which divisions this user belongs to: those folders are theirs to fill.
  // If /me cannot be read, assume both and let the backend refuse.
  const [myModules, setMyModules] = useState(["sekretaris", "bendahara"]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busyId, setBusyId] = useState(null);

  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState("");
  const [folders, setFolders] = useState({ sekretaris_folder_id: "", bendahara_folder_id: "" });
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");

  useEffect(() => {
    apiFetch("/api/auth/me/")
      .then((me) => {
        const mine = (me.accessible_modules || []).filter((m) => MODULES.some((x) => x.key === m));
        if (mine.length) setMyModules(mine);
      })
      .catch(() => {});
  }, []);

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const data = await apiFetch("/api/administrasi/events/");
      setEvents(Array.isArray(data) ? data : data.results || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const current = events.find((e) => e.is_active) || null;       // newest first
  const strayActive = current ? events.filter((e) => e.is_active && e.id !== current.id) : [];
  const past = events.filter((e) => !e.is_active);

  const setActive = async (ev, active) => {
    const verb = active ? "Aktifkan lagi" : "Akhiri";
    const ask = active
      ? `Aktifkan lagi event "${ev.name}"?`
      : `Akhiri event "${ev.name}"? Semua role berhenti bekerja di event ini sampai event baru dimulai.`;
    if (!window.confirm(ask)) return;
    setBusyId(ev.id);
    setError("");
    try {
      await apiFetch(`/api/administrasi/events/${ev.id}/`, {
        method: "PATCH",
        body: JSON.stringify({ is_active: active }),
      });
      fetchAll();
    } catch (e) {
      setError(`${verb} gagal: ${e.message}`);
    } finally {
      setBusyId(null);
    }
  };

  const rename = async (ev) => {
    const next = window.prompt("Nama event", ev.name);
    if (next === null || !next.trim() || next.trim() === ev.name) return;
    setBusyId(ev.id);
    setError("");
    try {
      await apiFetch(`/api/administrasi/events/${ev.id}/`, {
        method: "PATCH",
        body: JSON.stringify({ name: next.trim() }),
      });
      fetchAll();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusyId(null);
    }
  };

  const openCreate = () => {
    setName("");
    setFolders({ sekretaris_folder_id: "", bendahara_folder_id: "" });
    setFormError("");
    setShowForm(true);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setFormError("");
    if (!name.trim()) {
      setFormError("Nama event wajib diisi.");
      return;
    }
    setSaving(true);
    try {
      // Only the folders this user may fill are sent.
      const payload = { name: name.trim(), is_active: true };
      MODULES.filter((m) => myModules.includes(m.key)).forEach((m) => {
        payload[m.field] = folders[m.field].trim();
      });
      await apiFetch("/api/administrasi/events/", { method: "POST", body: JSON.stringify(payload) });
      setShowForm(false);
      fetchAll();
    } catch (err) {
      setFormError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col gap-8">
      <div>
        <h1 className="text-2xl font-semibold">Event &amp; Folder</h1>
        <p className="text-sm text-muted mt-1">
          Semua role bekerja di <strong>satu event aktif</strong> yang sama. Sekretaris dan
          Bendahara sama-sama bisa memulai dan mengakhirinya, lalu masing-masing mengisi folder
          Drive divisinya sendiri.
        </p>
      </div>

      {error && <div className="note-danger">{error}</div>}
      {loading && <p className="text-sm text-muted">Loading...</p>}

      {!loading && strayActive.length > 0 && (
        <div className="db-card" style={{ borderColor: "color-mix(in srgb, var(--amber) 45%, transparent)" }}>
          <span className="text-sm" style={{ color: "var(--amber)" }}>
            Ada lebih dari satu event aktif (data lama). Semua role memakai yang terbaru,
            &ldquo;{current.name}&rdquo;. Akhiri sisanya:
          </span>
          {strayActive.map((ev) => (
            <div key={ev.id} className="flex items-center justify-between gap-3">
              <span className="text-sm font-semibold">{ev.name}</span>
              <button
                type="button"
                onClick={() => setActive(ev, false)}
                disabled={busyId === ev.id}
                className="btn-outline btn-sm"
              >
                Akhiri
              </button>
            </div>
          ))}
        </div>
      )}

      {!loading && current && (
        <section className="flex flex-col gap-4">
          <div className="db-card !flex-row !items-center !justify-between !gap-4 flex-wrap">
            <div className="flex flex-col gap-1 min-w-0">
              <span className="eyebrow">EVENT AKTIF</span>
              <span className="text-xl font-semibold truncate">{current.name}</span>
              <span className="text-xs text-muted">Dibuat oleh {current.created_by_username || "-"}</span>
            </div>
            <div className="flex gap-2">
              <button type="button" onClick={() => rename(current)} disabled={busyId === current.id} className="btn-outline btn-sm">
                Ganti nama
              </button>
              <button type="button" onClick={() => setActive(current, false)} disabled={busyId === current.id} className="btn-danger btn-sm">
                Akhiri event
              </button>
            </div>
          </div>

          <div className="db-grid">
            {MODULES.map((m) => (
              <FolderCard
                key={m.key}
                event={current}
                mod={m}
                mine={myModules.includes(m.key)}
                onChanged={fetchAll}
              />
            ))}
          </div>
        </section>
      )}

      {!loading && !current && (
        <div className="db-card !flex-row !items-center !justify-between !gap-4 flex-wrap">
          <div className="flex flex-col gap-1">
            <span className="text-[15px] font-semibold">Belum ada event aktif</span>
            <span className="text-[13px]" style={{ color: "var(--text-dim)" }}>
              Mulai event supaya surat, anggaran, dan kas bisa dikerjakan.
            </span>
          </div>
          <button type="button" onClick={openCreate} className="btn-primary">
            + Mulai Event Baru
          </button>
        </div>
      )}

      {!loading && current && (
        <p className="text-xs text-muted">
          Mau memulai event baru? Akhiri event aktif di atas dulu: hanya satu event yang boleh
          berjalan.
        </p>
      )}

      {!loading && past.length > 0 && (
        <section className="flex flex-col gap-3">
          <div className="eyebrow">RIWAYAT EVENT</div>
          <div className="glass-table-wrap">
            {past.map((ev) => (
              <div
                key={ev.id}
                className="flex items-center justify-between gap-3 px-5 py-3 border-t border-[var(--border-subtle)] first:border-t-0"
              >
                <div className="min-w-0 flex flex-col">
                  <span className="text-sm font-semibold truncate">{ev.name}</span>
                  <span className="db-code">
                    DIBUAT {new Date(ev.created_at).toLocaleDateString("id-ID")} · {ev.created_by_username || "-"}
                  </span>
                </div>
                {!current && (
                  <button
                    type="button"
                    onClick={() => setActive(ev, true)}
                    disabled={busyId === ev.id}
                    className="btn-outline btn-sm shrink-0"
                  >
                    Aktifkan lagi
                  </button>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="glass-card w-full max-w-lg p-6">
            <h2 className="text-lg font-semibold mb-4">Mulai Event Baru</h2>
            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <label className="block text-xs font-medium mb-1">Nama Event</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="glass-input"
                  placeholder="mis. Mujahadah Kubro Rojab 1448 H"
                  autoFocus
                />
              </div>
              {MODULES.filter((m) => myModules.includes(m.key)).map((m) => (
                <div key={m.key}>
                  <label className="block text-xs font-medium mb-1">Folder ID {m.label}</label>
                  <input
                    type="text"
                    value={folders[m.field]}
                    onChange={(e) => setFolders((f) => ({ ...f, [m.field]: e.target.value }))}
                    className="glass-input font-mono text-xs"
                    placeholder="Tempel Folder ID dari URL Drive (boleh diisi nanti)"
                  />
                </div>
              ))}
              <p className="text-xs text-muted">
                Folder divisi lain diisi oleh divisinya sendiri setelah event dibuat.
              </p>

              {formError && <div className="note-danger">{formError}</div>}

              <div className="flex gap-2 justify-end pt-2">
                <button type="button" onClick={() => setShowForm(false)} className="btn-ghost">
                  Batal
                </button>
                <button type="submit" disabled={saving} className="btn-primary">
                  {saving ? "Menyimpan..." : "Mulai Event"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
